import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ensureSubdomain } from '../../../utils/subdomain';

export const Route = createFileRoute('/subdomain/dev/graph')({
  beforeLoad: ensureSubdomain('dev'),
  component: GraphifyKnowledgeGraphPage,
});

interface RawNode {
  id: string;
  label: string;
  source_file?: string;
  source_location?: string;
  community?: number;
  community_name?: string;
  file_type?: string;
  degree?: number;
  _callable?: boolean;
  _callable_class?: boolean;
}

interface RawLink {
  source: string;
  target: string;
  relation?: string;
  confidence?: string;
  source_file?: string;
  source_location?: string;
}

interface GraphData {
  nodes: RawNode[];
  links: RawLink[];
}

interface SimNode extends RawNode {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  pkg: string;
  inDegree: number;
  outDegree: number;
  totalDegree: number;
  radius: number;
  isGodNode: boolean;
  isDts: boolean;
}

interface SimLink {
  source: SimNode;
  target: SimNode;
  relation: string;
  isCrossPackage: boolean;
  isCrossCommunity: boolean;
}

interface PackageTheme {
  label: string;
  name: string;
  color: string;
  borderColor: string;
  bgTint: string;
  boundaryRadius: number;
  center: { x: number; y: number };
}

// Tableau 20 Categorical Palette (matching Graphify's default community visualization)
const COMMUNITY_PALETTE = [
  '#4E79A7', // Blue
  '#F28E2B', // Orange
  '#E15759', // Red
  '#76B7B2', // Teal
  '#59A14F', // Green
  '#EDC948', // Yellow
  '#B07AA1', // Purple
  '#FF9DA7', // Pink
  '#9C755F', // Brown
  '#BAB0AC', // Gray
  '#A0CBE8', // Light Blue
  '#FFBE7D', // Light Orange
  '#FF9D9A', // Light Red
  '#86BCB6', // Light Teal
  '#8CD17D', // Light Green
  '#F1CE63', // Light Yellow
  '#D4A6C8', // Light Purple
  '#FABFD2', // Light Pink
  '#D7B5A6', // Light Brown
  '#79706E', // Slate Gray
];

// Expanded cluster centers for spacious layout
const PACKAGE_THEMES: Record<string, PackageTheme> = {
  api: {
    label: 'APPS / API',
    name: 'Backend API (Hono & Edge)',
    color: '#38bdf8', // Electric Sky Blue
    borderColor: '#0284c7',
    bgTint: 'rgba(56, 189, 248, 0.04)',
    boundaryRadius: 480,
    center: { x: 0, y: -720 },
  },
  web: {
    label: 'APPS / WEB',
    name: 'Web Portal (TanStack Router & React)',
    color: '#34d399', // Mint Emerald
    borderColor: '#059669',
    bgTint: 'rgba(52, 211, 153, 0.04)',
    boundaryRadius: 500,
    center: { x: -960, y: -40 },
  },
  mobile: {
    label: 'APPS / MOBILE',
    name: 'Mobile App (Expo & React Native)',
    color: '#c084fc', // Vivid Purple
    borderColor: '#9333ea',
    bgTint: 'rgba(192, 132, 252, 0.04)',
    boundaryRadius: 500,
    center: { x: -560, y: 760 },
  },
  db: {
    label: 'PACKAGES / DB',
    name: 'Database Schema & Drizzle ORM',
    color: '#fbbf24', // Warm Amber Gold
    borderColor: '#d97706',
    bgTint: 'rgba(251, 191, 36, 0.04)',
    boundaryRadius: 500,
    center: { x: 960, y: -40 },
  },
  'api-client': {
    label: 'PACKAGES / API-CLIENT',
    name: 'Type-Safe RPC Client',
    color: '#fb7185', // Radiant Rose
    borderColor: '#e11d48',
    bgTint: 'rgba(251, 113, 133, 0.05)',
    boundaryRadius: 280,
    center: { x: 40, y: 160 },
  },
  'ui-tokens': {
    label: 'PACKAGES / UI-TOKENS',
    name: 'Design System & Tokens',
    color: '#818cf8', // Indigo Lavender
    borderColor: '#4f46e5',
    bgTint: 'rgba(129, 140, 248, 0.04)',
    boundaryRadius: 360,
    center: { x: 560, y: 760 },
  },
  root: {
    label: 'ROOT / CONFIG',
    name: 'Monorepo Root & Build Config',
    color: '#94a3b8', // Cool Slate
    borderColor: '#64748b',
    bgTint: 'rgba(148, 163, 184, 0.03)',
    boundaryRadius: 240,
    center: { x: 0, y: 0 },
  },
};

interface CommunityMeta {
  id: number;
  name: string;
  count: number;
  color: string;
  topNodeId?: string;
}

function getPackageKey(filePath?: string): string {
  if (!filePath) return 'root';
  if (filePath.startsWith('apps/api')) return 'api';
  if (filePath.startsWith('apps/web')) return 'web';
  if (filePath.startsWith('apps/mobile')) return 'mobile';
  if (filePath.startsWith('packages/db')) return 'db';
  if (filePath.startsWith('packages/api-client')) return 'api-client';
  if (filePath.startsWith('packages/ui-tokens')) return 'ui-tokens';
  return 'root';
}

function getNodeCategory(node: RawNode): string {
  if (node._callable_class) return 'CLASS';
  if (node._callable) return 'FUNCTION';
  if (node.label.endsWith('.ts') || node.label.endsWith('.tsx')) return 'MODULE';
  if (node.file_type === 'doc') return 'DOC';
  return 'SYMBOL';
}

type ColorMode = 'package' | 'community';
type LayoutMode = 'layered' | 'community' | 'package' | 'radial';

function computeLayoutTargets(
  nodes: SimNode[],
  mode: LayoutMode,
  spreadLevel: number,
  communitiesList: CommunityMeta[],
) {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));

  if (mode === 'layered') {
    // 4 Architectural Tiers:
    // Tier 1 (Presentation): web (left), mobile (right) -> Y ≈ -620
    // Tier 2 (RPC Client): api-client (center) -> Y ≈ -180
    // Tier 3 (Edge Server): api (center spread) -> Y ≈ 240
    // Tier 4 (Persistence & Shared): db (left), ui-tokens (right), root (center) -> Y ≈ 720
    const tierCenters: Record<string, { cx: number; cy: number; widthSpread: number }> = {
      web: { cx: -540, cy: -620, widthSpread: 1.7 },
      mobile: { cx: 540, cy: -620, widthSpread: 1.6 },
      'api-client': { cx: 0, cy: -180, widthSpread: 2.0 },
      api: { cx: 0, cy: 240, widthSpread: 2.3 },
      db: { cx: -540, cy: 720, widthSpread: 1.6 },
      'ui-tokens': { cx: 540, cy: 720, widthSpread: 1.6 },
      root: { cx: 0, cy: 720, widthSpread: 1.2 },
    };

    const buckets: Record<string, SimNode[]> = {};
    nodes.forEach((n) => {
      buckets[n.pkg] = buckets[n.pkg] || [];
      buckets[n.pkg].push(n);
    });

    Object.entries(buckets).forEach(([pkg, bNodes]) => {
      const cfg = tierCenters[pkg] || tierCenters.root;
      const count = bNodes.length;
      if (count === 0) return;
      const clusterRadius = Math.max(Math.sqrt(count) * 25 * spreadLevel, 120);

      bNodes.forEach((node, i) => {
        const radius = Math.sqrt((i + 1) / count) * clusterRadius;
        const theta = i * goldenAngle;
        node.targetX = (cfg.cx + radius * Math.cos(theta) * cfg.widthSpread) * spreadLevel;
        node.targetY = (cfg.cy + radius * Math.sin(theta) * 0.75) * spreadLevel;
      });
    });
  } else if (mode === 'community') {
    const commBuckets: Record<number, SimNode[]> = {};
    nodes.forEach((n) => {
      const cid = n.community ?? 0;
      commBuckets[cid] = commBuckets[cid] || [];
      commBuckets[cid].push(n);
    });

    const activeComms = communitiesList.filter((c) => (commBuckets[c.id]?.length || 0) > 0);
    const topN = Math.min(activeComms.length, 12);
    const primaryRadius = 880 * spreadLevel;
    const secondaryRadius = 1550 * spreadLevel;

    activeComms.forEach((comm, idx) => {
      const bNodes = commBuckets[comm.id] || [];
      const count = bNodes.length;
      if (count === 0) return;

      let cx = 0;
      let cy = 0;
      if (idx < topN) {
        const angle = (idx / topN) * Math.PI * 2 - Math.PI / 2;
        cx = Math.cos(angle) * primaryRadius;
        cy = Math.sin(angle) * primaryRadius;
      } else {
        const angle = ((idx - topN) / Math.max(1, activeComms.length - topN)) * Math.PI * 2;
        cx = Math.cos(angle) * secondaryRadius;
        cy = Math.sin(angle) * secondaryRadius;
      }

      const clusterRadius = Math.max(Math.sqrt(count) * 26 * spreadLevel, 80);
      bNodes.forEach((node, i) => {
        const radius = Math.sqrt((i + 1) / count) * clusterRadius;
        const theta = i * goldenAngle;
        node.targetX = cx + radius * Math.cos(theta);
        node.targetY = cy + radius * Math.sin(theta);
      });
    });
  } else if (mode === 'radial') {
    const sorted = [...nodes].sort((a, b) => b.totalDegree - a.totalDegree);
    const count = sorted.length;

    sorted.forEach((node, i) => {
      let r;
      if (node.totalDegree >= 12) {
        r = (40 + (i / 20) * 180) * spreadLevel;
      } else if (node.totalDegree >= 4) {
        r = (320 + Math.sqrt(i / count) * 380) * spreadLevel;
      } else {
        r = (780 + Math.sqrt(i / count) * 580) * spreadLevel;
      }

      const theta = i * goldenAngle;
      node.targetX = r * Math.cos(theta);
      node.targetY = r * Math.sin(theta);
    });
  } else {
    // 'package' layout
    const buckets: Record<string, SimNode[]> = {};
    nodes.forEach((n) => {
      buckets[n.pkg] = buckets[n.pkg] || [];
      buckets[n.pkg].push(n);
    });

    Object.entries(buckets).forEach(([pkg, bNodes]) => {
      const theme = PACKAGE_THEMES[pkg] || PACKAGE_THEMES.root;
      const count = bNodes.length;
      if (count === 0) return;

      const clusterRadius = Math.max(Math.sqrt(count) * 26 * spreadLevel, 140);
      bNodes.forEach((node, i) => {
        const radius = Math.sqrt((i + 1) / count) * clusterRadius;
        const theta = i * goldenAngle;
        node.targetX = theme.center.x * spreadLevel + radius * Math.cos(theta);
        node.targetY = theme.center.y * spreadLevel + radius * Math.sin(theta);
      });
    });
  }
}

function GraphifyKnowledgeGraphPage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Raw data & loading state
  const [data, setData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Layout mode: 'layered' (DAG flow), 'community' (Louvain clusters), 'package' (folders), 'radial' (core-periphery)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('layered');

  // Color mode: 'package' vs 'community'
  const [colorMode, setColorMode] = useState<ColorMode>('community');

  // Decluttering controls
  const [hideGeneratedDts, setHideGeneratedDts] = useState<boolean>(true);
  const [spreadLevel, setSpreadLevel] = useState<number>(1.6); // 1.0 = compact, 1.6 = spacious, 2.2 = wide

  // Filters & display options
  const [activePackage, setActivePackage] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [showEdges, setShowEdges] = useState(true);

  // Right sidebar tab & open state
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [rightTab, setRightTab] = useState<'communities' | 'inspector'>('communities');

  // Community selection set (which communities are visible)
  const [activeCommunityIds, setActiveCommunityIds] = useState<Set<number>>(new Set());

  // Camera transform: x, y, zoom
  const cameraRef = useRef({ x: 0, y: 0, zoom: 0.55 });
  const isDraggingRef = useRef(false);
  const dragMovedRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Fetch graph.json on mount
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetch('/graphify.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}: failed to fetch graphify.json`);
        return res.json();
      })
      .then((json: GraphData) => {
        if (!isMounted) return;
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Failed to load graphify.json:', err);
        setError(err.message || 'Failed to load graph.json');
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Process & layout nodes
  const { nodes, links, nodeMap, adjacency, godNodes, communitiesList, communityColorMap } = useMemo(() => {
    if (!data) {
      return {
        nodes: [] as SimNode[],
        links: [] as SimLink[],
        nodeMap: new Map<string, SimNode>(),
        adjacency: new Map<string, Set<string>>(),
        godNodes: [] as SimNode[],
        communitiesList: [] as CommunityMeta[],
        communityColorMap: new Map<number, string>(),
      };
    }

    const nMap = new Map<string, SimNode>();
    const adj = new Map<string, Set<string>>();

    // Filter out auto-generated .d.ts if hideGeneratedDts is enabled
    const filteredRawNodes = data.nodes.filter((raw) => {
      if (hideGeneratedDts && raw.source_file && raw.source_file.endsWith('.d.ts')) {
        return false;
      }
      return true;
    });

    // Group nodes by package for deterministic layout
    const packageBuckets: Record<string, RawNode[]> = {
      api: [],
      web: [],
      mobile: [],
      db: [],
      'api-client': [],
      'ui-tokens': [],
      root: [],
    };

    const communityBuckets: Record<number, RawNode[]> = {};

    const simNodes: SimNode[] = [];

    for (const raw of filteredRawNodes) {
      const pkg = getPackageKey(raw.source_file);
      packageBuckets[pkg] = packageBuckets[pkg] || [];
      packageBuckets[pkg].push(raw);

      const commId = raw.community ?? 0;
      communityBuckets[commId] = communityBuckets[commId] || [];
      communityBuckets[commId].push(raw);

      const isDts = !!(raw.source_file && raw.source_file.endsWith('.d.ts'));

      const node: SimNode = {
        ...raw,
        x: 0,
        y: 0,
        targetX: 0,
        targetY: 0,
        pkg,
        inDegree: 0,
        outDegree: 0,
        totalDegree: raw.degree || 0,
        radius: 4,
        isGodNode: false,
        isDts,
      };

      simNodes.push(node);
      nMap.set(node.id, node);
      adj.set(node.id, new Set<string>());
    }

    // Build resolved links & degrees
    const simLinks: SimLink[] = [];
    const rawLinks = data.links || [];

    for (const raw of rawLinks) {
      const source = nMap.get(raw.source);
      const target = nMap.get(raw.target);
      if (source && target) {
        source.outDegree += 1;
        target.inDegree += 1;
        source.totalDegree = source.inDegree + source.outDegree;
        target.totalDegree = target.inDegree + target.outDegree;

        simLinks.push({
          source,
          target,
          relation: raw.relation || 'related_to',
          isCrossPackage: source.pkg !== target.pkg,
          isCrossCommunity: source.community !== target.community,
        });

        adj.get(source.id)?.add(target.id);
        adj.get(target.id)?.add(source.id);
      }
    }

    // Compute node radius & identify architectural god nodes
    simNodes.forEach((node) => {
      let r = 4.5;
      if (node.totalDegree > 2) r = 6;
      if (node.totalDegree > 6) r = 8;
      if (node.totalDegree > 14) r = 11;
      if (node.totalDegree > 25) r = 14;

      node.radius = r;
      node.isGodNode =
        node.totalDegree >= 6 ||
        node.label.endsWith('.ts') ||
        node.label.endsWith('.tsx') ||
        node.label.endsWith('Route');
    });

    // Compute Communities metadata sorted by size
    const commEntries = Object.entries(communityBuckets).map(([cidStr, bucket]) => {
      const cid = Number(cidStr);
      const topNode = [...bucket].sort((a, b) => (b.degree || 0) - (a.degree || 0))[0];
      const name = topNode?.label || `Community ${cid}`;
      return {
        id: cid,
        name,
        count: bucket.length,
        color: '',
        topNodeId: topNode?.id,
      };
    });

    commEntries.sort((a, b) => b.count - a.count);

    const commColorMap = new Map<number, string>();
    commEntries.forEach((comm, idx) => {
      const color = COMMUNITY_PALETTE[idx % COMMUNITY_PALETTE.length];
      comm.color = color;
      commColorMap.set(comm.id, color);
    });

    const topGodNodes = [...simNodes].sort((a, b) => b.totalDegree - a.totalDegree).slice(0, 20);

    // Compute layout target coordinates and set initial positions
    computeLayoutTargets(simNodes, layoutMode, spreadLevel, commEntries);
    simNodes.forEach((node) => {
      node.x = node.targetX;
      node.y = node.targetY;
    });

    return {
      nodes: simNodes,
      links: simLinks,
      nodeMap: nMap,
      adjacency: adj,
      godNodes: topGodNodes,
      communitiesList: commEntries,
      communityColorMap: commColorMap,
    };
  }, [data, hideGeneratedDts, spreadLevel, layoutMode]);

  // Sync active communities once data loads (only initialize once!)
  const hasInitializedCommunities = useRef(false);
  useEffect(() => {
    if (communitiesList.length > 0 && !hasInitializedCommunities.current) {
      setActiveCommunityIds(new Set(communitiesList.map((c) => c.id)));
      hasInitializedCommunities.current = true;
    }
  }, [communitiesList]);

  // Filtered nodes
  const filteredNodes = useMemo(() => {
    let result = nodes;
    if (activePackage !== 'all') {
      result = result.filter((n) => n.pkg === activePackage);
    }
    if (colorMode === 'community') {
      result = result.filter((n) => activeCommunityIds.has(n.community ?? 0));
    }
    return result;
  }, [nodes, activePackage, colorMode, activeCommunityIds]);

  const filteredNodeSet = useMemo(() => new Set(filteredNodes.map((n) => n.id)), [filteredNodes]);

  // Filtered links
  const filteredLinks = useMemo(() => {
    return links.filter((l) => filteredNodeSet.has(l.source.id) && filteredNodeSet.has(l.target.id));
  }, [links, filteredNodeSet]);

  // Selected node details
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    return nodeMap.get(selectedNodeId) || null;
  }, [selectedNodeId, nodeMap]);

  // Hovered node details
  const hoveredNode = useMemo(() => {
    if (!hoveredNodeId) return null;
    return nodeMap.get(hoveredNodeId) || null;
  }, [hoveredNodeId, nodeMap]);

  // Direct 1-hop neighbors of selected or hovered node
  const activeFocusId = selectedNodeId || hoveredNodeId;
  const focusedNeighbors = useMemo(() => {
    if (!activeFocusId) return null;
    const neighbors = adjacency.get(activeFocusId);
    return neighbors ? new Set(neighbors) : null;
  }, [activeFocusId, adjacency]);

  // Relations for selected node
  const selectedRelations = useMemo(() => {
    if (!selectedNodeId) return { inbound: [] as SimLink[], outbound: [] as SimLink[] };
    const inbound: SimLink[] = [];
    const outbound: SimLink[] = [];

    for (const link of links) {
      if (link.target.id === selectedNodeId) inbound.push(link);
      if (link.source.id === selectedNodeId) outbound.push(link);
    }

    return { inbound, outbound };
  }, [selectedNodeId, links]);

  // Search match set
  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const set = new Set<string>();
    for (const node of nodes) {
      if (node.label.toLowerCase().includes(q) || (node.source_file && node.source_file.toLowerCase().includes(q))) {
        set.add(node.id);
      }
    }
    return set;
  }, [searchQuery, nodes]);

  // Reset / Fit camera view
  const fitView = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || filteredNodes.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const n of filteredNodes) {
      if (n.x < minX) minX = n.x;
      if (n.x > maxX) maxX = n.x;
      if (n.y < minY) minY = n.y;
      if (n.y > maxY) maxY = n.y;
    }

    const width = canvas.width / (window.devicePixelRatio || 1);
    const height = canvas.height / (window.devicePixelRatio || 1);

    const graphWidth = maxX - minX + 280;
    const graphHeight = maxY - minY + 280;

    const zoom = Math.min(width / graphWidth, height / graphHeight, 1.0);
    cameraRef.current = {
      x: width / 2 - ((minX + maxX) / 2) * zoom,
      y: height / 2 - ((minY + maxY) / 2) * zoom,
      zoom: Math.max(0.18, zoom),
    };
  }, [filteredNodes]);

  // Jump camera to specific node
  const jumpToNode = useCallback((node: SimNode) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.width / (window.devicePixelRatio || 1);
    const height = canvas.height / (window.devicePixelRatio || 1);
    const zoom = 1.35;

    cameraRef.current = {
      x: width / 2 - node.x * zoom,
      y: height / 2 - node.y * zoom,
      zoom,
    };
    setSelectedNodeId(node.id);
    setRightTab('inspector');
    setRightPanelOpen(true);
  }, []);

  // Community list actions
  const toggleCommunity = (cid: number) => {
    setActiveCommunityIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) {
        next.delete(cid);
      } else {
        next.add(cid);
      }
      return next;
    });
  };

  const allCommunitiesSelected = communitiesList.length > 0 && activeCommunityIds.size === communitiesList.length;

  const toggleAllCommunities = () => {
    if (allCommunitiesSelected) {
      setActiveCommunityIds(new Set());
    } else {
      setActiveCommunityIds(new Set(communitiesList.map((c) => c.id)));
    }
  };

  // Helper to get color for any node
  const getNodeColor = useCallback(
    (node: SimNode) => {
      if (colorMode === 'community') {
        return communityColorMap.get(node.community ?? 0) || '#BAB0AC';
      }
      return PACKAGE_THEMES[node.pkg]?.color || '#ffffff';
    },
    [colorMode, communityColorMap],
  );

  // ==========================================
  // MULTI-LAYER HIGH-PRECISION RENDER LOOP
  // ==========================================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const render = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
      }

      ctx.save();
      ctx.scale(dpr, dpr);

      // Deep rich charcoal canvas background
      ctx.fillStyle = '#0a0b0e';
      ctx.fillRect(0, 0, width, height);

      const { x: camX, y: camY, zoom } = cameraRef.current;

      // Draw subtle CAD coordinate grid
      const gridSize = 50 * zoom;
      if (gridSize > 12) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.07)';
        const startX = (camX % gridSize) - gridSize;
        const startY = (camY % gridSize) - gridSize;

        for (let gx = startX; gx < width + gridSize; gx += gridSize) {
          for (let gy = startY; gy < height + gridSize; gy += gridSize) {
            ctx.fillRect(gx - 0.75, gy - 0.75, 1.5, 1.5);
          }
        }
      }

      // Transform space for graph world
      ctx.save();
      ctx.translate(camX, camY);
      ctx.scale(zoom, zoom);

      // Fluid node position interpolation towards layout target coordinates
      for (const node of filteredNodes) {
        const dx = node.targetX - node.x;
        const dy = node.targetY - node.y;
        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
          node.x += dx * 0.16;
          node.y += dy * 0.16;
        } else {
          node.x = node.targetX;
          node.y = node.targetY;
        }
      }

      // ==========================================
      // PASS 1: Layout-Specific Architectural Backgrounds
      // ==========================================
      if (layoutMode === 'layered') {
        const tiers = [
          {
            label: 'TIER 1 // CLIENTS & PRESENTATION (apps/web · apps/mobile)',
            y: -620 * spreadLevel,
            h: 360 * spreadLevel,
            color: '#34d399',
            bgTint: 'rgba(52, 211, 153, 0.02)',
            borderColor: 'rgba(52, 211, 153, 0.22)',
          },
          {
            label: 'TIER 2 // CONTRACTS & RPC CLIENT (packages/api-client)',
            y: -180 * spreadLevel,
            h: 220 * spreadLevel,
            color: '#fb7185',
            bgTint: 'rgba(251, 113, 133, 0.02)',
            borderColor: 'rgba(251, 113, 133, 0.22)',
          },
          {
            label: 'TIER 3 // EDGE SERVER & BACKEND API (apps/api)',
            y: 240 * spreadLevel,
            h: 360 * spreadLevel,
            color: '#38bdf8',
            bgTint: 'rgba(56, 189, 248, 0.02)',
            borderColor: 'rgba(56, 189, 248, 0.22)',
          },
          {
            label: 'TIER 4 // PERSISTENCE & TOKENS (packages/db · packages/ui-tokens)',
            y: 720 * spreadLevel,
            h: 340 * spreadLevel,
            color: '#fbbf24',
            bgTint: 'rgba(251, 191, 36, 0.02)',
            borderColor: 'rgba(251, 191, 36, 0.22)',
          },
        ];

        const laneWidth = 2600 * spreadLevel;
        tiers.forEach((tier) => {
          const top = tier.y - tier.h / 2;
          ctx.fillStyle = tier.bgTint;
          ctx.fillRect(-laneWidth / 2, top, laneWidth, tier.h);

          ctx.strokeStyle = tier.borderColor;
          ctx.lineWidth = 1;
          ctx.setLineDash([6, 6]);
          ctx.strokeRect(-laneWidth / 2, top, laneWidth, tier.h);
          ctx.setLineDash([]);

          ctx.font = 'bold 11px ui-monospace, SFMono-Regular, monospace';
          const tagMetrics = ctx.measureText(tier.label);
          const tagW = tagMetrics.width + 20;
          const tagH = 22;
          const tagX = -laneWidth / 2 + tagW / 2 + 20;
          const tagY = top + 14;

          ctx.fillStyle = '#0e1017';
          ctx.strokeStyle = tier.color;
          ctx.lineWidth = 1;
          ctx.fillRect(tagX - tagW / 2, tagY - tagH / 2, tagW, tagH);
          ctx.strokeRect(tagX - tagW / 2, tagY - tagH / 2, tagW, tagH);

          ctx.fillStyle = tier.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(tier.label, tagX, tagY);
        });

        // Downward Flow Arrows
        const arrows = [
          ((-620 + -180) / 2) * spreadLevel,
          ((-180 + 240) / 2) * spreadLevel,
          ((240 + 720) / 2) * spreadLevel,
        ];
        arrows.forEach((ay) => {
          ctx.fillStyle = 'rgba(161, 161, 170, 0.35)';
          ctx.font = '14px ui-monospace, monospace';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('▼', 0, ay);
        });
      } else if (layoutMode === 'community') {
        const topComms = communitiesList.slice(0, 12);
        const commRadius = 880 * spreadLevel;
        topComms.forEach((comm, idx) => {
          const angle = (idx / topComms.length) * Math.PI * 2 - Math.PI / 2;
          const cx = Math.cos(angle) * commRadius;
          const cy = Math.sin(angle) * commRadius;
          const r = Math.max(Math.sqrt(comm.count) * 26 * spreadLevel, 90);

          ctx.fillStyle = `${comm.color}08`;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();

          ctx.strokeStyle = `${comm.color}35`;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          ctx.setLineDash([]);

          const tag = `[ ${comm.name} · ${comm.count} ]`;
          ctx.font = 'bold 10px ui-monospace, monospace';
          const tm = ctx.measureText(tag);
          const tw = tm.width + 16;
          const th = 20;
          const ty = cy - r - 12;

          ctx.fillStyle = '#0f1118';
          ctx.strokeStyle = comm.color;
          ctx.lineWidth = 1;
          ctx.fillRect(cx - tw / 2, ty - th / 2, tw, th);
          ctx.strokeRect(cx - tw / 2, ty - th / 2, tw, th);

          ctx.fillStyle = comm.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(tag, cx, ty);
        });
      } else if (layoutMode === 'radial') {
        const rings = [
          { r: 240 * spreadLevel, label: 'CORE // ARCHITECTURAL HUBS (DEGREE ≥ 12)', color: '#38bdf8' },
          { r: 720 * spreadLevel, label: 'MID-ORBIT // SERVICES & CONTROLLERS (DEGREE 4–11)', color: '#c084fc' },
          { r: 1350 * spreadLevel, label: 'PERIPHERY // LEAF FUNCTIONS & HELPERS (DEGREE ≤ 3)', color: '#94a3b8' },
        ];
        rings.forEach((ring) => {
          ctx.strokeStyle = `${ring.color}25`;
          ctx.lineWidth = 1;
          ctx.setLineDash([6, 8]);
          ctx.beginPath();
          ctx.arc(0, 0, ring.r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);

          ctx.font = 'bold 10px ui-monospace, monospace';
          const tm = ctx.measureText(ring.label);
          const tw = tm.width + 16;
          const th = 18;
          ctx.fillStyle = '#0c0d12';
          ctx.strokeStyle = `${ring.color}60`;
          ctx.fillRect(-tw / 2, -ring.r - th / 2, tw, th);
          ctx.strokeRect(-tw / 2, -ring.r - th / 2, tw, th);

          ctx.fillStyle = ring.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(ring.label, 0, -ring.r);
        });
      } else {
        Object.entries(PACKAGE_THEMES).forEach(([pkg, theme]) => {
          if (activePackage !== 'all' && activePackage !== pkg) return;

          const cx = theme.center.x * spreadLevel;
          const cy = theme.center.y * spreadLevel;
          const r = theme.boundaryRadius * (spreadLevel * 0.85);

          ctx.fillStyle = theme.bgTint;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();

          ctx.strokeStyle = theme.color;
          ctx.lineWidth = 1;
          ctx.setLineDash([6, 6]);
          ctx.stroke();
          ctx.setLineDash([]);

          const tagText = `[ ${theme.label} ]`;
          ctx.font = 'bold 11px ui-monospace, SFMono-Regular, monospace';
          const textMetrics = ctx.measureText(tagText);
          const tagWidth = textMetrics.width + 18;
          const tagHeight = 22;
          const tagY = cy - r - 14;

          ctx.fillStyle = '#111319';
          ctx.strokeStyle = theme.borderColor;
          ctx.lineWidth = 1.25;
          ctx.fillRect(cx - tagWidth / 2, tagY - tagHeight / 2, tagWidth, tagHeight);
          ctx.strokeRect(cx - tagWidth / 2, tagY - tagHeight / 2, tagWidth, tagHeight);

          ctx.fillStyle = theme.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(tagText, cx, tagY);
        });
      }

      // ==========================================
      // PASS 2: Unselected & Background Edges
      // ==========================================
      if (showEdges) {
        for (const link of filteredLinks) {
          const isConnectedToFocus =
            activeFocusId && (link.source.id === activeFocusId || link.target.id === activeFocusId);

          if (isConnectedToFocus) continue; // Drawn with high priority in Pass 3

          // let strokeStyle = 'rgba(203, 213, 225, 0.45)';
          // let lineWidth = 1.25;
          let strokeStyle;
          let lineWidth;

          if (activeFocusId) {
            // Unselected edges while a node is focused: keep them clearly visible as architectural background
            if (colorMode === 'community') {
              const cColor = communityColorMap.get(link.source.community ?? 0);
              if (cColor && !link.isCrossCommunity) {
                strokeStyle = `${cColor}40`; // ~25% opacity
                lineWidth = 0.95;
              } else {
                strokeStyle = 'rgba(148, 163, 184, 0.22)';
                lineWidth = 1.0;
              }
            } else if (link.isCrossPackage) {
              strokeStyle = 'rgba(251, 191, 36, 0.38)';
              lineWidth = 1.2;
            } else {
              const srcTheme = PACKAGE_THEMES[link.source.pkg];
              if (srcTheme) {
                strokeStyle = `${srcTheme.color}35`;
                lineWidth = 0.95;
              } else {
                strokeStyle = 'rgba(148, 163, 184, 0.22)';
                lineWidth = 0.95;
              }
            }
          } else {
            // Idle state: connection lines are crisp, vibrant, and unmistakable
            if (colorMode === 'community') {
              const cColor = communityColorMap.get(link.source.community ?? 0);
              if (cColor && !link.isCrossCommunity) {
                strokeStyle = `${cColor}80`; // 50% opacity in community palette
                lineWidth = 1.3;
              } else {
                strokeStyle = 'rgba(203, 213, 225, 0.45)'; // 45% crisp slate for cross-community links
                lineWidth = 1.4;
              }
            } else if (link.isCrossPackage) {
              strokeStyle = 'rgba(251, 191, 36, 0.70)'; // 70% golden amber for cross-package links
              lineWidth = 1.5;
            } else {
              const srcTheme = PACKAGE_THEMES[link.source.pkg];
              if (srcTheme) {
                strokeStyle = `${srcTheme.color}70`; // 44% package theme color
                lineWidth = 1.3;
              } else {
                strokeStyle = 'rgba(203, 213, 225, 0.45)';
                lineWidth = 1.3;
              }
            }
          }

          ctx.strokeStyle = strokeStyle;
          ctx.lineWidth = lineWidth;
          ctx.beginPath();
          ctx.moveTo(link.source.x, link.source.y);
          ctx.lineTo(link.target.x, link.target.y);
          ctx.stroke();
        }
      }

      // ==========================================
      // PASS 3: Highlighted / Active Focus Edges
      // ==========================================
      if (showEdges && activeFocusId) {
        for (const link of filteredLinks) {
          const isConnectedToFocus = link.source.id === activeFocusId || link.target.id === activeFocusId;
          if (!isConnectedToFocus) continue;

          const isOutbound = link.source.id === activeFocusId;
          ctx.strokeStyle = isOutbound ? '#38bdf8' : '#34d399';
          ctx.lineWidth = 3.0;
          ctx.beginPath();
          ctx.moveTo(link.source.x, link.source.y);
          ctx.lineTo(link.target.x, link.target.y);
          ctx.stroke();

          // Flow dot at link center
          const midX = (link.source.x + link.target.x) / 2;
          const midY = (link.source.y + link.target.y) / 2;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(midX, midY, 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // ==========================================
      // PASS 4: Normal Nodes
      // ==========================================
      for (const node of filteredNodes) {
        const isSelected = node.id === selectedNodeId;
        const isHovered = node.id === hoveredNodeId;
        const isNeighbor = focusedNeighbors?.has(node.id);
        const isFocus = isSelected || isHovered || isNeighbor;

        if (isFocus) continue; // Drawn later in top layers!

        const isSearchMatch = searchMatches ? searchMatches.has(node.id) : false;
        const isDimmed = activeFocusId || (searchMatches && !isSearchMatch);

        const nodeColor = getNodeColor(node);
        const nodeRadius = node.radius;

        let fillStyle = nodeColor;
        let strokeStyle = nodeColor;
        let lineWidth = 1.25;

        if (isDimmed) {
          fillStyle = '#16181f';
          strokeStyle = '#222530';
          lineWidth = 0.75;
        } else if (isSearchMatch) {
          fillStyle = '#ffffff';
          strokeStyle = '#f59e0b';
          lineWidth = 2.5;
        }

        ctx.fillStyle = fillStyle;
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = lineWidth;

        ctx.beginPath();
        if (node._callable_class) {
          const s = nodeRadius * 1.6;
          ctx.rect(node.x - s / 2, node.y - s / 2, s, s);
        } else {
          ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.stroke();

        // Inner pip for god nodes
        if (node.isGodNode && !isDimmed) {
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(node.x, node.y, Math.max(nodeRadius * 0.35, 1.5), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // ==========================================
      // PASS 5: Static Labels (God Nodes / Auto Labels)
      // ==========================================
      if (!activeFocusId) {
        for (const node of filteredNodes) {
          const isSearchMatch = searchMatches ? searchMatches.has(node.id) : false;
          const shouldShow =
            isSearchMatch || (node.isGodNode && zoom > 0.45) || showAllLabels || (zoom > 1.3 && node.totalDegree >= 2);

          if (!shouldShow) continue;

          const labelText = node.label;
          const themeColor = getNodeColor(node);

          ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
          const textMetrics = ctx.measureText(labelText);
          const bgPaddingX = 5;
          const boxWidth = textMetrics.width + bgPaddingX * 2;
          const boxHeight = 16;
          const boxX = node.x + node.radius + 4;
          const boxY = node.y - boxHeight / 2;

          ctx.fillStyle = 'rgba(11, 13, 18, 0.9)';
          ctx.strokeStyle = `${themeColor}77`;
          ctx.lineWidth = 1;
          ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
          ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

          ctx.fillStyle = themeColor;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(labelText, boxX + bgPaddingX, node.y);
        }
      }

      // ==========================================
      // PASS 6: Focused 1-Hop Neighbor Nodes & Labels
      // ==========================================
      if (activeFocusId && focusedNeighbors) {
        for (const node of filteredNodes) {
          if (!focusedNeighbors.has(node.id) || node.id === activeFocusId) continue;

          const nodeColor = getNodeColor(node);
          const nodeRadius = node.radius + 1;

          // Bright neighbor node
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = nodeColor;
          ctx.lineWidth = 2.5;

          ctx.beginPath();
          if (node._callable_class) {
            const s = nodeRadius * 1.6;
            ctx.rect(node.x - s / 2, node.y - s / 2, s, s);
          } else {
            ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
          }
          ctx.fill();
          ctx.stroke();

          // Neighbor label chip
          const labelText = node.label;
          ctx.font = 'bold 11px ui-monospace, SFMono-Regular, monospace';
          const textMetrics = ctx.measureText(labelText);
          const bgPaddingX = 6;
          const boxWidth = textMetrics.width + bgPaddingX * 2;
          const boxHeight = 18;
          const boxX = node.x + nodeRadius + 5;
          const boxY = node.y - boxHeight / 2;

          ctx.fillStyle = '#141724';
          ctx.strokeStyle = nodeColor;
          ctx.lineWidth = 1.25;
          ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
          ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(labelText, boxX + bgPaddingX, node.y);
        }
      }

      // ==========================================
      // PASS 7 (TOP PRIORITY): HOVERED & SELECTED NODE
      // GUARANTEED ABOVE ALL ELSE
      // ==========================================
      const topFocusNode = hoveredNode || selectedNode;

      if (topFocusNode && filteredNodeSet.has(topFocusNode.id)) {
        const node = topFocusNode;
        const nodeColor = getNodeColor(node);
        const nodeRadius = node.radius + 3;

        // 1. Prominent glowing halo ring
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius + 4, 0, Math.PI * 2);
        ctx.stroke();

        // 2. Focused node body
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = nodeColor;
        ctx.lineWidth = 3.5;

        ctx.beginPath();
        if (node._callable_class) {
          const s = nodeRadius * 1.7;
          ctx.rect(node.x - s / 2, node.y - s / 2, s, s);
        } else {
          ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.stroke();

        // 3. Architectural Reticle Brackets
        const r = nodeRadius + 9;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.75;
        const corner = 5;

        // Top-left
        ctx.beginPath();
        ctx.moveTo(node.x - r, node.y - r + corner);
        ctx.lineTo(node.x - r, node.y - r);
        ctx.lineTo(node.x - r + corner, node.y - r);
        ctx.stroke();

        // Top-right
        ctx.beginPath();
        ctx.moveTo(node.x + r - corner, node.y - r);
        ctx.lineTo(node.x + r, node.y - r);
        ctx.lineTo(node.x + r, node.y - r + corner);
        ctx.stroke();

        // Bottom-left
        ctx.beginPath();
        ctx.moveTo(node.x - r, node.y + r - corner);
        ctx.lineTo(node.x - r, node.y + r);
        ctx.lineTo(node.x - r + corner, node.y + r);
        ctx.stroke();

        // Bottom-right
        ctx.beginPath();
        ctx.moveTo(node.x + r - corner, node.y + r);
        ctx.lineTo(node.x + r, node.y + r);
        ctx.lineTo(node.x + r, node.y + r - corner);
        ctx.stroke();

        // 4. FLOATING HIGH-CONTRAST CARD (ABOVE ALL OTHER ELEMENTS)
        const titleText = node.label;
        const catText = `[${getNodeCategory(node)}]`;
        const pathText = node.source_file
          ? `${node.source_file}${node.source_location ? `:${node.source_location}` : ''}`
          : 'external';
        const degreeText = `${node.inDegree} in · ${node.outDegree} out`;
        const metaText = `${node.pkg.toUpperCase()}${node.community !== undefined ? ` · Comm #${node.community}` : ''}`;

        ctx.font = 'bold 12px ui-monospace, SFMono-Regular, monospace';
        const titleWidth = ctx.measureText(titleText).width;

        ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
        const catWidth = ctx.measureText(catText).width;
        const pathWidth = ctx.measureText(pathText).width;
        const degreeWidth = ctx.measureText(degreeText).width;
        const metaWidth = ctx.measureText(metaText).width;

        // Structured 3-row layout: compute width from row max with generous padding
        const row1Width = titleWidth + catWidth + 28;
        const row2Width = pathWidth + 24;
        const row3Width = degreeWidth + metaWidth + 28;
        const cardWidth = Math.min(Math.max(row1Width, row2Width, row3Width, 220), 400);
        const cardHeight = 64;

        // Auto-flip if card would overflow canvas right edge
        const worldRightEdge = (-camX + width) / zoom;
        let cardX = node.x + nodeRadius + 12;
        if (cardX + cardWidth > worldRightEdge - 20 / zoom) {
          cardX = node.x - nodeRadius - 12 - cardWidth;
        }
        const cardY = node.y - cardHeight / 2;

        // Truncate path with ellipsis if it exceeds card width
        let displayPath = pathText;
        if (pathWidth > cardWidth - 24) {
          while (displayPath.length > 8 && ctx.measureText(`…${displayPath}`).width > cardWidth - 24) {
            displayPath = displayPath.slice(2);
          }
          displayPath = `…${displayPath}`;
        }

        // Drop shadow backing
        ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
        ctx.fillRect(cardX + 2, cardY + 2, cardWidth, cardHeight);

        // Solid opaque card body
        ctx.fillStyle = '#0f1118';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
        ctx.strokeRect(cardX, cardY, cardWidth, cardHeight);

        // Row 1: Symbol Label & Category Tag
        ctx.font = 'bold 12px ui-monospace, SFMono-Regular, monospace';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(titleText, cardX + 10, cardY + 8);

        ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
        ctx.fillStyle = nodeColor;
        ctx.textAlign = 'right';
        ctx.fillText(catText, cardX + cardWidth - 10, cardY + 9);

        // Row 2: File location (dedicated row, no collision!)
        ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
        ctx.fillStyle = '#94a3b8';
        ctx.textAlign = 'left';
        ctx.fillText(displayPath, cardX + 10, cardY + 27);

        // Row 3: In/Out degree & Package / Community
        ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
        ctx.fillStyle = '#e2e8f0';
        ctx.textAlign = 'left';
        ctx.fillText(degreeText, cardX + 10, cardY + 45);

        ctx.fillStyle = '#94a3b8';
        ctx.textAlign = 'right';
        ctx.fillText(metaText, cardX + cardWidth - 10, cardY + 45);
      }

      ctx.restore(); // Restore world transform

      // ==========================================
      // PASS 8: Radar Mini-Map (Bottom Right)
      // ==========================================
      if (filteredNodes.length > 0) {
        const mmWidth = 170;
        const mmHeight = 115;
        // Lifted 64px from bottom to sit comfortably above any bottom overlay/badge (e.g. 'Host: dev')
        const mmX = width - mmWidth - 18;
        const mmY = height - mmHeight - 64;

        ctx.save();

        // 1. Draw outer mini-map frame
        ctx.fillStyle = '#151824';
        ctx.strokeStyle = '#3b4259';
        ctx.lineWidth = 1;
        ctx.fillRect(mmX, mmY, mmWidth, mmHeight);
        ctx.strokeRect(mmX, mmY, mmWidth, mmHeight);

        // Header title
        ctx.fillStyle = '#93c5fd';
        ctx.font = 'bold 9px ui-monospace, SFMono-Regular, monospace';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('RADAR // OVERVIEW', mmX + 8, mmY + 6);

        // 2. Compute true bounding box of the graph in world space
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;

        for (const n of filteredNodes) {
          if (n.x < minX) minX = n.x;
          if (n.x > maxX) maxX = n.x;
          if (n.y < minY) minY = n.y;
          if (n.y > maxY) maxY = n.y;
        }

        const graphW = Math.max(maxX - minX, 100);
        const graphH = Math.max(maxY - minY, 100);
        const graphCenterX = (minX + maxX) / 2;
        const graphCenterY = (minY + maxY) / 2;

        // Drawable interior of mini-map (below header)
        const padX = 10;
        const padTop = 20;
        const padBottom = 8;
        const innerW = mmWidth - padX * 2;
        const innerH = mmHeight - padTop - padBottom;
        const innerCenterX = mmX + padX + innerW / 2;
        const innerCenterY = mmY + padTop + innerH / 2;

        // Dynamic scale factor: entire graph fits with padding
        const scale = Math.min(innerW / (graphW * 1.15), innerH / (graphH * 1.15));

        // 3. Clip strictly to mini-map inner boundary
        ctx.save();
        ctx.beginPath();
        ctx.rect(mmX + 1, mmY + 18, mmWidth - 2, mmHeight - 19);
        ctx.clip();

        // 4. Draw node dots
        for (const node of filteredNodes) {
          const nx = innerCenterX + (node.x - graphCenterX) * scale;
          const ny = innerCenterY + (node.y - graphCenterY) * scale;

          const dotColor = getNodeColor(node);
          ctx.fillStyle = node.id === selectedNodeId ? '#ffffff' : dotColor;
          const dotSize = node.id === selectedNodeId ? 3 : node.isGodNode ? 2.5 : 1.75;
          ctx.fillRect(nx - dotSize / 2, ny - dotSize / 2, dotSize, dotSize);
        }

        // 5. Accurate Viewport Camera Frustum
        const worldLeft = -camX / zoom;
        const worldTop = -camY / zoom;
        const worldW = width / zoom;
        const worldH = height / zoom;

        // Map world viewport to mini-map coordinates
        const vpX = innerCenterX + (worldLeft - graphCenterX) * scale;
        const vpY = innerCenterY + (worldTop - graphCenterY) * scale;
        const vpW = worldW * scale;
        const vpH = worldH * scale;

        // Viewport rectangle fill & border
        ctx.fillStyle = 'rgba(56, 189, 248, 0.08)';
        ctx.fillRect(vpX, vpY, vpW, vpH);

        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 1.25;
        ctx.strokeRect(vpX, vpY, vpW, vpH);

        // Corner tick accents on viewport rectangle
        const tick = Math.min(4, Math.max(2, vpW / 4), Math.max(2, vpH / 4));
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        // top-left
        ctx.beginPath();
        ctx.moveTo(vpX, vpY + tick);
        ctx.lineTo(vpX, vpY);
        ctx.lineTo(vpX + tick, vpY);
        // bottom-right
        ctx.moveTo(vpX + vpW - tick, vpY + vpH);
        ctx.lineTo(vpX + vpW, vpY + vpH);
        ctx.lineTo(vpX + vpW, vpY + vpH - tick);
        ctx.stroke();

        ctx.restore(); // Restore clip

        // Subtle crosshair at graph center
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(innerCenterX - 4, innerCenterY);
        ctx.lineTo(innerCenterX + 4, innerCenterY);
        ctx.moveTo(innerCenterX, innerCenterY - 4);
        ctx.lineTo(innerCenterX, innerCenterY + 4);
        ctx.stroke();

        ctx.restore(); // Restore device state
      }

      ctx.restore(); // Restore devicePixelRatio
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [
    filteredNodes,
    filteredLinks,
    selectedNodeId,
    hoveredNodeId,
    focusedNeighbors,
    searchMatches,
    activePackage,
    showAllLabels,
    showEdges,
    colorMode,
    communityColorMap,
    getNodeColor,
    spreadLevel,
    layoutMode,
    selectedNode,
    hoveredNode,
    filteredNodeSet,
  ]);

  const hasInitialFitRef = useRef(false);

  // Initial camera fit ONCE when nodes first load
  useEffect(() => {
    if (nodes.length > 0 && !hasInitialFitRef.current) {
      fitView();
      hasInitialFitRef.current = true;
    }
  }, [nodes, fitView]);

  // Compute smooth morph targets whenever layoutMode, spreadLevel, or communities change (WITHOUT resetting camera!)
  useEffect(() => {
    if (nodes.length > 0) {
      computeLayoutTargets(nodes, layoutMode, spreadLevel, communitiesList);
    }
  }, [nodes, layoutMode, spreadLevel, communitiesList]);

  // Pointer Event Handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return; // Left click only

    const canvas = canvasRef.current;
    if (canvas && filteredNodes.length > 0) {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const mmWidth = 170;
      const mmHeight = 115;
      const mmX = rect.width - mmWidth - 18;
      const mmY = rect.height - mmHeight - 64;

      if (mouseX >= mmX && mouseX <= mmX + mmWidth && mouseY >= mmY && mouseY <= mmY + mmHeight) {
        // User clicked inside Radar Mini-Map: navigate camera to clicked point!
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (const n of filteredNodes) {
          if (n.x < minX) minX = n.x;
          if (n.x > maxX) maxX = n.x;
          if (n.y < minY) minY = n.y;
          if (n.y > maxY) maxY = n.y;
        }

        const graphW = Math.max(maxX - minX, 100);
        const graphH = Math.max(maxY - minY, 100);
        const graphCenterX = (minX + maxX) / 2;
        const graphCenterY = (minY + maxY) / 2;

        const padX = 10;
        const padTop = 20;
        const padBottom = 8;
        const innerW = mmWidth - padX * 2;
        const innerH = mmHeight - padTop - padBottom;
        const innerCenterX = mmX + padX + innerW / 2;
        const innerCenterY = mmY + padTop + innerH / 2;

        const scale = Math.min(innerW / (graphW * 1.15), innerH / (graphH * 1.15));

        const clickedWorldX = graphCenterX + (mouseX - innerCenterX) / scale;
        const clickedWorldY = graphCenterY + (mouseY - innerCenterY) / scale;

        const curZoom = cameraRef.current.zoom;
        cameraRef.current.x = rect.width / 2 - clickedWorldX * curZoom;
        cameraRef.current.y = rect.height / 2 - clickedWorldY * curZoom;
        return;
      }
    }

    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dragMovedRef.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (isDraggingRef.current) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      if (Math.hypot(dx, dy) > 3) {
        dragMovedRef.current = true;
      }
      cameraRef.current.x += dx;
      cameraRef.current.y += dy;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    // Hover hit test
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const { x: camX, y: camY, zoom } = cameraRef.current;
    const worldX = (mouseX - camX) / zoom;
    const worldY = (mouseY - camY) / zoom;

    let hitNode: SimNode | null = null;
    const hitRadius = 18 / zoom;

    for (let i = filteredNodes.length - 1; i >= 0; i--) {
      const n = filteredNodes[i];
      const dx = n.x - worldX;
      const dy = n.y - worldY;
      if (dx * dx + dy * dy <= hitRadius * hitRadius) {
        hitNode = n;
        break;
      }
    }

    setHoveredNodeId(hitNode ? hitNode.id : null);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDraggingRef.current) return;
    const wasDrag = dragMovedRef.current;
    isDraggingRef.current = false;

    if (wasDrag) return;

    // Check if was click without drag
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const { x: camX, y: camY, zoom } = cameraRef.current;
    const worldX = (mouseX - camX) / zoom;
    const worldY = (mouseY - camY) / zoom;

    const hitRadius = 18 / zoom;
    let hitNode: SimNode | null = null;

    for (let i = filteredNodes.length - 1; i >= 0; i--) {
      const n = filteredNodes[i];
      const dx = n.x - worldX;
      const dy = n.y - worldY;
      if (dx * dx + dy * dy <= hitRadius * hitRadius) {
        hitNode = n;
        break;
      }
    }

    if (hitNode) {
      setSelectedNodeId(hitNode.id);
      setRightTab('inspector');
      setRightPanelOpen(true);
    } else {
      setSelectedNodeId(null);
    }
  };

  // Touchpad & Mouse Wheel Handler (2-finger Pan, Pinch / Ctrl+Scroll Zoom)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (e.ctrlKey || e.metaKey) {
        // Pinch-to-zoom on touchpad OR Ctrl+Scroll: ZOOM centered at cursor
        // Math.exp gives mathematically smooth scaling regardless of scroll speed
        const delta = -e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.007);
        const zoomFactor = Math.exp(delta);
        const currentZoom = cameraRef.current.zoom;
        const newZoom = Math.min(Math.max(currentZoom * zoomFactor, 0.08), 5.0);

        cameraRef.current.x = mouseX - (mouseX - cameraRef.current.x) * (newZoom / currentZoom);
        cameraRef.current.y = mouseY - (mouseY - cameraRef.current.y) * (newZoom / currentZoom);
        cameraRef.current.zoom = newZoom;
      } else {
        // 2-finger swipe on touchpad: PAN viewport in X and Y
        const unit = e.deltaMode === 1 ? 24 : 1;
        cameraRef.current.x -= e.deltaX * unit;
        cameraRef.current.y -= e.deltaY * unit;
      }
    };

    // Prevent Safari gesture interference
    const onGesture = (e: Event) => {
      e.preventDefault();
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('gesturestart', onGesture);
    canvas.addEventListener('gesturechange', onGesture);

    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('gesturestart', onGesture);
      canvas.removeEventListener('gesturechange', onGesture);
    };
  }, []);

  // Keep latest fitView in ref for keydown listener
  const fitViewRef = useRef(fitView);
  useEffect(() => {
    fitViewRef.current = fitView;
  }, [fitView]);

  // Keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      const isInput = activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA';

      if (isInput) {
        if (e.key === 'Escape') {
          (activeEl as HTMLElement)?.blur();
        }
        return;
      }

      if (e.key === 'Escape') {
        setSelectedNodeId(null);
        setSearchQuery('');
      } else if (e.key === '/' || e.key === '?') {
        e.preventDefault();
        const input = document.getElementById('graph-search') as HTMLInputElement | null;
        input?.focus();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        fitViewRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex flex-col h-screen w-screen bg-[#0a0b0e] text-[#f4f4f5] overflow-hidden font-sans select-none">
      {/* Top Architectural Navbar: Full screen width, strictly above everything! */}
      <header className="h-12 w-full z-30 flex items-center justify-between px-4 border-b border-[#3b4259] bg-[#151824] shrink-0 gap-3">
        {/* Left Metadata & Back Link */}
        <div className="flex items-center gap-3 shrink-0 whitespace-nowrap">
          <Link
            to="/subdomain/dev"
            className="text-xs font-mono text-[#cbd5e1] hover:text-white transition flex items-center gap-1.5 shrink-0 whitespace-nowrap"
          >
            <span className="text-[#38bdf8] font-bold">←</span>
            <span>DEV HUB</span>
          </Link>

          <span className="text-[#64748b] shrink-0">|</span>

          <div className="shrink-0 whitespace-nowrap leading-tight">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-mono font-bold tracking-wider text-white">GRAPHIFY</span>
              <span className="text-[9px] font-mono text-[#93c5fd] font-bold bg-[#1e2a3f] px-1.5 py-0.5 border border-[#38bdf8]/40">
                ARCHITECTURE
              </span>
            </div>
            <div className="text-[10px] font-mono text-[#cbd5e1] mt-0.5">
              <span className="text-white font-semibold">{filteredNodes.length.toLocaleString()}</span> /{' '}
              <span className="text-[#94a3b8]">{nodes.length.toLocaleString()} NODES</span>
            </div>
          </div>
        </div>

        {/* Center: Search & Mode Toggle */}
        <div className="flex items-center gap-2.5">
          {/* Search Input */}
          <div className="relative">
            <input
              id="graph-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="filter symbols [/]..."
              className="w-36 focus:w-48 transition-all duration-150 h-7 px-2 text-xs font-mono bg-[#1f2436] border border-[#424b66] focus:border-[#38bdf8] text-white outline-none rounded-none placeholder:text-[#94a3b8]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1.5 text-[10px] font-mono text-[#cbd5e1] hover:text-white"
              >
                ESC
              </button>
            )}
          </div>

          {/* LAYOUT SELECTOR: Layered (DAG) vs Community vs Packages vs Radial */}
          <div className="flex items-center gap-1 border-l border-[#3b4259] pl-2.5 text-[11px] font-mono">
            <span className="text-[#93c5fd] mr-1 text-[10px] font-bold">LAYOUT:</span>
            {[
              {
                id: 'layered',
                label: 'DAG',
                tip: 'Layered (DAG): Architectural top-to-bottom pipeline: Web/Mobile → API-Client → API → DB',
              },
              { id: 'community', label: 'COMMUNITY', tip: 'Louvain call-graph semantic domain clusters' },
              { id: 'package', label: 'PACKAGES', tip: 'Monorepo folder package clusters' },
              { id: 'radial', label: 'RADIAL', tip: 'Concentric core-periphery orbit around architectural god-nodes' },
            ].map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  setLayoutMode(l.id as LayoutMode);
                  if (l.id === 'community') setColorMode('community');
                  if (l.id === 'package') setColorMode('package');
                }}
                className={`px-1.5 py-1 border text-[10px] transition font-mono ${
                  layoutMode === l.id
                    ? 'bg-[#1e3a5f] border-[#38bdf8] text-white font-bold shadow-xs'
                    : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:border-[#60a5fa] hover:text-white'
                }`}
                title={l.tip}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* COLOR MODE TOGGLE: Packages vs Communities */}
          <div className="flex items-center gap-1 border-l border-[#3b4259] pl-2.5 text-[11px] font-mono">
            <span className="text-[#93c5fd] mr-1 text-[10px] font-bold">COLOR BY:</span>
            <button
              onClick={() => setColorMode('package')}
              className={`px-2 py-1 transition flex items-center gap-1.5 border text-xs ${
                colorMode === 'package'
                  ? 'bg-[#1e3a5f] border-[#38bdf8] text-white font-bold shadow-xs'
                  : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
              }`}
              title="Color nodes by monorepo apps and packages"
            >
              <span>PACKAGES</span>
            </button>
            <button
              onClick={() => {
                setColorMode('community');
                setRightTab('communities');
                setRightPanelOpen(true);
              }}
              className={`px-2 py-1 transition flex items-center gap-1.5 border text-xs ${
                colorMode === 'community'
                  ? 'bg-[#3b1f50] border-[#c084fc] text-white font-bold shadow-xs'
                  : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
              }`}
              title="Color nodes by Graphify detected communities"
            >
              <span>COMMUNITIES</span>
            </button>
          </div>

          {/* Density / Spacing Control */}
          <div className="flex items-center gap-1 border-l border-[#3b4259] pl-2.5 text-[11px] font-mono">
            <span className="text-[#93c5fd] mr-1 text-[10px] font-bold">SPREAD:</span>
            {[
              { label: '1x', val: 1.0 },
              { label: '1.6x', val: 1.6 },
              { label: '2.2x', val: 2.2 },
            ].map((sp) => (
              <button
                key={sp.label}
                onClick={() => setSpreadLevel(sp.val)}
                className={`px-1.5 py-0.5 border text-[10px] transition ${
                  spreadLevel === sp.val
                    ? 'bg-white text-black font-bold border-white'
                    : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
                }`}
              >
                {sp.label}
              </button>
            ))}
          </div>

          {/* Toggle Generated .d.ts files (Crucial for decluttering!) */}
          <button
            onClick={() => setHideGeneratedDts(!hideGeneratedDts)}
            className={`px-2 py-1 border text-[10px] font-mono transition ${
              hideGeneratedDts
                ? 'bg-[#153826] border-[#34d399] text-[#34d399] font-bold'
                : 'bg-[#3e1b20] border-red-500 text-red-200 font-bold'
            }`}
            title="Filter out 1,788 auto-generated worker-configuration types"
          >
            TYPES: {hideGeneratedDts ? 'CLEAN' : 'ALL'}
          </button>
        </div>

        {/* Right Viewport Controls & Panel Toggle */}
        <div className="flex items-center gap-2 text-xs font-mono">
          {/* Toggle Labels */}
          <button
            onClick={() => setShowAllLabels(!showAllLabels)}
            className={`px-2 py-1 border text-[10px] transition ${
              showAllLabels
                ? 'bg-white text-black border-white font-bold'
                : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
            }`}
            title="Toggle Labels Always On"
          >
            LABELS: {showAllLabels ? 'ALL' : 'AUTO'}
          </button>

          {/* Toggle Edges */}
          <button
            onClick={() => setShowEdges(!showEdges)}
            className={`px-2 py-1 border text-[10px] transition ${
              showEdges
                ? 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
                : 'bg-red-950/60 border-red-500 text-red-200 font-bold'
            }`}
            title="Toggle Edge Lines"
          >
            EDGES
          </button>

          <span className="text-[#64748b]">|</span>

          <button
            onClick={() => {
              cameraRef.current.zoom = Math.min(cameraRef.current.zoom * 1.25, 4.5);
            }}
            className="px-2 py-1 bg-[#1f2436] border border-[#424b66] hover:border-[#60a5fa] text-[#e2e8f0] hover:text-white transition"
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={() => {
              cameraRef.current.zoom = Math.max(cameraRef.current.zoom * 0.8, 0.1);
            }}
            className="px-2 py-1 bg-[#1f2436] border border-[#424b66] hover:border-[#60a5fa] text-[#e2e8f0] hover:text-white transition"
            title="Zoom Out"
          >
            -
          </button>
          <button
            onClick={fitView}
            className="px-2.5 py-1 bg-[#1f2436] border border-[#424b66] hover:border-[#60a5fa] text-[#e2e8f0] hover:text-white transition text-[10px] font-semibold tracking-wider"
            title="Fit View"
          >
            FIT
          </button>

          <button
            onClick={() => setRightPanelOpen(!rightPanelOpen)}
            className={`px-2.5 py-1 border text-[10px] font-bold transition ${
              rightPanelOpen
                ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                : 'bg-[#1f2436] border-[#424b66] text-[#e2e8f0] hover:bg-[#2b324a] hover:text-white'
            }`}
            title="Toggle Sidebar Panel"
          >
            PANEL
          </button>
        </div>
      </header>

      {/* Main Workspace (Strictly below the navbar) */}
      <div className="flex flex-1 w-full h-[calc(100vh-3rem)] overflow-hidden relative">
        {/* Canvas Viewport */}
        <div className="relative flex-1 h-full w-full overflow-hidden">
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            className="h-full w-full cursor-crosshair touch-none"
          />

          {/* Floating Quick Anchors Bar (Top Left) */}
          <div className="absolute top-3 left-5 z-10 flex items-center gap-1.5 text-[10px] font-mono bg-[#151824]/95 border border-[#3b4259] p-1.5 backdrop-blur-sm shadow-md">
            <span className="text-[#93c5fd] font-bold px-1">ANCHORS:</span>
            {godNodes.slice(0, 6).map((gn) => {
              const nodeColor = getNodeColor(gn);
              return (
                <button
                  key={gn.id}
                  onClick={() => jumpToNode(gn)}
                  className="px-2 py-0.5 bg-[#1f2436] hover:bg-[#2b324a] border border-[#424b66] hover:border-[#38bdf8] transition truncate max-w-32 font-medium"
                  style={{ color: nodeColor }}
                  title={`${gn.label} (${gn.totalDegree} connections)`}
                >
                  {gn.label}
                </button>
              );
            })}
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#09090b]/85 backdrop-blur-sm font-mono text-xs text-[#cbd5e1]">
              <div className="w-6 h-6 border-2 border-[#424b66] border-t-white animate-spin mb-3" />
              <div>LOADING GRAPHIFY KNOWLEDGE GRAPH...</div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="absolute top-16 left-6 z-20 max-w-md p-3 bg-[#18181b] border border-red-800 font-mono text-xs text-red-200">
              <div className="font-bold mb-1">[ERROR LOADING GRAPH DATA]</div>
              <div>{error}</div>
              <div className="mt-2 text-[10px] text-[#94a3b8]">
                Ensure graphify-out/graph.json exists (run `graphify update .`); the dev server serves it at
                /graphify.json.
              </div>
            </div>
          )}

          {/* Bottom Left Navigation Status & Touchpad HUD */}
          <div className="absolute bottom-4 left-5 z-10 font-mono text-[10px] text-[#cbd5e1] bg-[#151824]/95 border border-[#3b4259] px-2.5 py-1 backdrop-blur-sm shadow-md flex items-center gap-3">
            {searchMatches ? (
              <span>
                MATCHES: <span className="text-white font-bold">{searchMatches.size}</span> / {filteredNodes.length}{' '}
                NODES
              </span>
            ) : (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="text-[#38bdf8] font-bold uppercase">{layoutMode}</span>
                  <span className="text-[#64748b]">·</span>
                  <span className="text-white font-medium">TOUCHPAD</span>
                  <span>2-finger pan · Pinch / Ctrl+wheel zoom</span>
                </span>
                <span className="text-[#64748b]">|</span>
                <span className="text-[#93c5fd]">[F] fit · [/] search · [ESC] clear</span>
              </>
            )}
          </div>
        </div>

        {/* Right-Hand Panel: COMMUNITIES or INSPECTOR (Starts strictly below the navbar) */}
        {rightPanelOpen && (
          <aside className="w-80 h-full border-l border-[#3b4259] bg-[#151824] flex flex-col z-20 overflow-hidden font-mono">
            {/* Panel Tabs */}
            <div className="flex items-center border-b border-[#3b4259] bg-[#1a1f30] text-xs font-semibold">
              <button
                onClick={() => setRightTab('communities')}
                className={`flex-1 py-2.5 text-center border-b-2 transition flex items-center justify-center gap-1.5 ${
                  rightTab === 'communities'
                    ? 'border-[#c084fc] text-white bg-[#261d36]'
                    : 'border-transparent text-[#cbd5e1] hover:text-white hover:bg-[#22273a]'
                }`}
              >
                <span>🧬</span>
                <span>COMMUNITIES</span>
                <span className="text-[10px] font-normal text-[#93c5fd]">({communitiesList.length})</span>
              </button>
              <button
                onClick={() => setRightTab('inspector')}
                className={`flex-1 py-2.5 text-center border-b-2 transition flex items-center justify-center gap-1.5 ${
                  rightTab === 'inspector'
                    ? 'border-[#38bdf8] text-white bg-[#1e2a3f]'
                    : 'border-transparent text-[#cbd5e1] hover:text-white hover:bg-[#22273a]'
                }`}
              >
                <span>🔍</span>
                <span>INSPECTOR</span>
              </button>
            </div>

            {/* TAB 1: COMMUNITIES LIST */}
            {rightTab === 'communities' && (
              <div className="flex-1 flex flex-col overflow-hidden p-3.5">
                {/* Communities Header & Select All */}
                <div className="flex items-center justify-between pb-2.5 mb-2 border-b border-[#3b4259]">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-white hover:text-[#93c5fd] select-none">
                    <input
                      type="checkbox"
                      checked={allCommunitiesSelected}
                      onChange={toggleAllCommunities}
                      className="accent-[#38bdf8] w-4 h-4 cursor-pointer"
                    />
                    <span>Select All</span>
                  </label>
                  <span className="text-[11px] text-[#cbd5e1] font-medium">
                    {activeCommunityIds.size} / {communitiesList.length} Active
                  </span>
                </div>

                {/* Scrollable Communities List */}
                <div className="flex-1 overflow-y-auto space-y-1 pr-1">
                  {communitiesList.map((comm) => {
                    const isChecked = activeCommunityIds.has(comm.id);
                    return (
                      <div
                        key={comm.id}
                        onClick={() => toggleCommunity(comm.id)}
                        className={`flex items-center justify-between py-1.5 px-2 rounded-none transition cursor-pointer text-xs ${
                          isChecked
                            ? 'bg-[#1f2436] hover:bg-[#282f46] text-white border border-[#333d59]'
                            : 'opacity-50 hover:opacity-85 text-[#94a3b8]'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 truncate max-w-52.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleCommunity(comm.id);
                            }}
                            className="accent-[#38bdf8] w-3.5 h-3.5 cursor-pointer shrink-0"
                          />
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: comm.color }} />
                          <span className="truncate font-mono font-medium" title={comm.name}>
                            {comm.name}
                          </span>
                        </div>
                        <span className="text-[11px] font-mono text-[#cbd5e1] shrink-0 font-medium">{comm.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB 2: INSPECTOR DETAILS */}
            {rightTab === 'inspector' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {selectedNode ? (
                  <>
                    {/* Selected Node Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#3b4259] bg-[#1a1f30]">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 border"
                          style={{
                            color: getNodeColor(selectedNode),
                            borderColor: getNodeColor(selectedNode),
                            backgroundColor: `${getNodeColor(selectedNode)}18`,
                          }}
                        >
                          {getNodeCategory(selectedNode)}
                        </span>
                        <span className="text-xs font-semibold text-white truncate max-w-44">{selectedNode.label}</span>
                      </div>
                      <button
                        onClick={() => setSelectedNodeId(null)}
                        className="text-[#94a3b8] hover:text-white text-xs px-1.5 py-0.5 hover:bg-[#282f46] rounded transition"
                        title="Clear Selection"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Inspector Body */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs text-[#e2e8f0]">
                      {/* Subsystem & Community info */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] text-[#93c5fd] font-bold uppercase tracking-wider mb-1">
                            Subsystem
                          </div>
                          <div
                            className="p-1.5 border text-[11px] font-medium truncate"
                            style={{
                              borderColor: PACKAGE_THEMES[selectedNode.pkg]?.borderColor,
                              backgroundColor: PACKAGE_THEMES[selectedNode.pkg]?.bgTint,
                              color: PACKAGE_THEMES[selectedNode.pkg]?.color,
                            }}
                          >
                            {PACKAGE_THEMES[selectedNode.pkg]?.name || selectedNode.pkg}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#93c5fd] font-bold uppercase tracking-wider mb-1">
                            Community
                          </div>
                          <div
                            className="p-1.5 border text-[11px] font-medium truncate"
                            style={{
                              borderColor: communityColorMap.get(selectedNode.community ?? 0),
                              backgroundColor: `${communityColorMap.get(selectedNode.community ?? 0)}15`,
                              color: communityColorMap.get(selectedNode.community ?? 0),
                            }}
                          >
                            #{selectedNode.community}
                          </div>
                        </div>
                      </div>

                      {/* Source Location */}
                      <div>
                        <div className="text-[10px] text-[#93c5fd] font-bold uppercase tracking-wider mb-1">
                          Source Location
                        </div>
                        <div className="p-2 bg-[#1f2436] border border-[#3b4259] text-[11px] break-all select-text text-white">
                          <span>{selectedNode.source_file || 'external'}</span>
                          {selectedNode.source_location && (
                            <span className="text-[#38bdf8] font-semibold">:{selectedNode.source_location}</span>
                          )}
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="p-2 bg-[#1f2436] border border-[#3b4259]">
                          <div className="text-lg font-bold text-white">{selectedNode.inDegree}</div>
                          <div className="text-[9px] text-[#93c5fd] uppercase font-bold">Inbound</div>
                        </div>
                        <div className="p-2 bg-[#1f2436] border border-[#3b4259]">
                          <div className="text-lg font-bold text-white">{selectedNode.outDegree}</div>
                          <div className="text-[9px] text-[#93c5fd] uppercase font-bold">Outbound</div>
                        </div>
                      </div>

                      {/* Inbound Dependencies */}
                      <div>
                        <div className="text-[10px] text-[#93c5fd] font-bold uppercase tracking-wider mb-1.5 flex items-center justify-between">
                          <span>Callers / Importers</span>
                          <span className="text-[#cbd5e1]">({selectedRelations.inbound.length})</span>
                        </div>
                        {selectedRelations.inbound.length === 0 ? (
                          <div className="text-[11px] text-[#94a3b8] italic">No inbound callers</div>
                        ) : (
                          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                            {selectedRelations.inbound.map((link, idx) => {
                              const nodeColor = getNodeColor(link.source);
                              return (
                                <button
                                  key={idx}
                                  onClick={() => jumpToNode(link.source)}
                                  className="w-full text-left p-1.5 bg-[#1f2436] hover:bg-[#282f46] border border-[#3b4259] hover:border-[#38bdf8] text-[11px] truncate transition flex items-center justify-between"
                                >
                                  <span className="truncate font-medium" style={{ color: nodeColor }}>
                                    {link.source.label}
                                  </span>
                                  <span className="text-[9px] text-[#cbd5e1] ml-2 shrink-0">{link.relation}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Outbound Dependencies */}
                      <div>
                        <div className="text-[10px] text-[#93c5fd] font-bold uppercase tracking-wider mb-1.5 flex items-center justify-between">
                          <span>Called / Imported</span>
                          <span className="text-[#cbd5e1]">({selectedRelations.outbound.length})</span>
                        </div>
                        {selectedRelations.outbound.length === 0 ? (
                          <div className="text-[11px] text-[#94a3b8] italic">No outbound calls</div>
                        ) : (
                          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                            {selectedRelations.outbound.map((link, idx) => {
                              const nodeColor = getNodeColor(link.target);
                              return (
                                <button
                                  key={idx}
                                  onClick={() => jumpToNode(link.target)}
                                  className="w-full text-left p-1.5 bg-[#1f2436] hover:bg-[#282f46] border border-[#3b4259] hover:border-[#34d399] text-[11px] truncate transition flex items-center justify-between"
                                >
                                  <span className="truncate font-medium" style={{ color: nodeColor }}>
                                    {link.target.label}
                                  </span>
                                  <span className="text-[9px] text-[#cbd5e1] ml-2 shrink-0">{link.relation}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-xs text-[#cbd5e1]">
                    <div className="text-2xl mb-2">🔍</div>
                    <div className="text-white font-medium mb-1">No Node Selected</div>
                    <div className="text-[#94a3b8]">
                      Click any node on the graph to inspect its callers and dependencies.
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
